// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Snackbar, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, IconButton, Tooltip, CircularProgress, Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import { QRCodeSVG } from 'qrcode.react';
import api from '../utils/api';
import ChampRecherche from '../components/ChampRecherche';
import ServerRow from '../components/serveurs/LigneServeur';
import { SecretField } from '../components/serveurs/commun';
import { printableDocument, printInFrame } from '../utils/impression';

/**
 * Serveurs Vigeos : serveurs d'établissement (N chambres, posés par une clé
 * gravée à l'atelier) et box de foyer.
 *
 * Les deux partagent la table `kits` et la route `/api/kits`. Le code d'un
 * serveur reste « VK-… » : il est gravé sur le matériel en service.
 *
 * La recherche est faite côté serveur : la liste est plafonnée à 200 lignes par
 * l'API, un filtre local ne verrait que les plus récentes. Le terme part à
 * `GET /api/kits?search=`, qui cherche dans le code, l'établissement, le
 * client, le logement et la localisation.
 */

const FILTER_TABS = [
  { value: '', label: 'Tous' },
  { value: 'prepared', label: 'Préparés' },
  { value: 'claimed', label: 'Activés' },
  { value: 'active', label: 'En service' },
  // Pas d'onglet « Retirés » : supprimer un serveur le supprime réellement (la
  // trace va au journal d'audit). Une ligne qui porte encore ce statut l'affiche.
];

const EMPTY_FORM = { hardware_model: 'RPi5-4GB', notes: '' };

export default function ServersPage() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [recherche, setRecherche] = useState('');
  // Terme envoyé à l'API, avec 300 ms de décalage : la recherche est faite côté
  // serveur, chaque frappe coûterait un aller-retour.
  const [submittedSearch, setSentSearch] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  // Création : le secret n'est retourné qu'une seule fois.
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSentSearch(recherche.trim()), 300);
    return () => clearTimeout(t);
  }, [recherche]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (submittedSearch) params.search = submittedSearch;
      const res = await api.get('/kits', { params });
      setServers(res.data.kits || []);
      setError('');
    } catch {
      setError('Impossible de charger les serveurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchServers(); }, [filterStatus, submittedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setToastSeverity('success');
    setToast('Copié dans le presse-papiers !');
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError('');
    try {
      const payload = {};
      if (createForm.hardware_model.trim()) payload.hardware_model = createForm.hardware_model.trim();
      if (createForm.notes.trim()) payload.notes = createForm.notes.trim();
      const res = await api.post('/kits', payload);
      setCreated(res.data);
      fetchServers();
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Échec de la création');
    } finally {
      setCreating(false);
    }
  };

  const closeCreateDialog = (event, reason) => {
    if (creating) return;
    // Un clic hors du dialogue ou Échap ne doit pas faire perdre le secret.
    if (created && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    setCreateOpen(false);
    setCreateForm(EMPTY_FORM);
    setCreated(null);
    setCreateError('');
  };

  // Étiquette imprimable, même gabarit que edge/box/tools/prepare_kit.py (100×62 mm).
  // Imprimée depuis un cadre isolé sans script (utils/impression.js), jamais par
  // window.open puis document.write dans l'origine du tableau de bord ; le code
  // du kit est échappé dans le titre.
  const printSticker = () => {
    const node = document.getElementById('kit-sticker-print');
    if (!node || !created) return;
    const html = printableDocument({
      titre: created.code,
      style: '@page { size: 100mm 62mm; margin: 0; } body { margin: 0; font-family: -apple-system, "Segoe UI", sans-serif; }',
      corps: node.outerHTML,
    });
    printInFrame(html, () => {
      setToastSeverity('warning');
      setToast('Impression impossible depuis ce navigateur : copier le code et le secret ci-dessous.');
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Serveurs</Typography>
          <Typography variant="body2" color="text.secondary">
            Le matériel Vigeos en service : serveurs d&apos;établissement et box de foyer.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={fetchServers}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateOpen(true); setCreateForm(EMPTY_FORM); setCreated(null); setCreateError(''); }}>
            Nouveau matériel
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Rechercher par nom, code, établissement ou localisation"
        />
      </Box>

      <Tabs
        value={filterStatus}
        onChange={(e, v) => setFilterStatus(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {FILTER_TABS.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>En ligne</TableCell>
              <TableCell>Client / Établissement</TableCell>
              <TableCell>Localisation</TableCell>
              <TableCell align="center">Caméras</TableCell>
              <TableCell>Modèle</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Créé le</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : servers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {submittedSearch
                    ? 'Aucun serveur ne correspond à cette recherche.'
                    : 'Aucun serveur — un serveur d’établissement se crée depuis « Clés de pose ».'}
                </TableCell>
              </TableRow>
            ) : (
              servers.map(s => (
                <ServerRow key={s.id} serveur={s} onCopy={handleCopy} onToast={setToast} onChanged={fetchServers} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Création : après le POST, le secret et le QR ne s'affichent qu'une fois. */}
      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{created ? `${created.code} créé` : 'Nouveau matériel'}</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          {created ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Le secret ne sera <strong>plus jamais affiché</strong> — imprimez l&rsquo;étiquette
                et copiez le secret dans les options de l&rsquo;add-on maintenant.
              </Alert>

              {/* Étiquette au gabarit du sticker atelier ; l'aperçu est la zone
                  imprimée. Elle reste claire dans ce dialogue sombre : elle sort
                  telle quelle sur un adhésif blanc. */}
              <div
                id="kit-sticker-print"
                style={{
                  width: '100mm', height: '62mm', boxSizing: 'border-box', padding: '6mm',
                  display: 'flex', alignItems: 'center', gap: '6mm',
                  border: '0.4mm dashed #94A3B8', borderRadius: '3mm',
                  background: '#fff', margin: '0 auto',
                }}
              >
                <QRCodeSVG value={created.qr_payload} size={166} style={{ width: '44mm', height: '44mm' }} />
                <div>
                  <div style={{ fontSize: '7mm', fontWeight: 800, color: '#0C1A33', marginBottom: '2mm' }}>Vigeos</div>
                  <div style={{ fontSize: '5.4mm', fontWeight: 700, letterSpacing: '0.6mm', color: '#0C1A33', marginBottom: '3mm' }}>
                    {created.code}
                  </div>
                  <div style={{ fontSize: '3.4mm', color: '#334155', lineHeight: 1.45 }}>
                    1. Branchez la box et la caméra.<br />
                    2. Ouvrez l&rsquo;app <strong>Vigeos</strong>.<br />
                    3. Scannez ce QR code — c&rsquo;est tout.
                  </div>
                </div>
              </div>

              <Grid container spacing={1.5} sx={{ mt: 1 }}>
                <SecretField label="Code" value={created.code} onCopy={handleCopy} />
                <SecretField label="Secret (one-time, options de l'add-on)" value={created.secret} onCopy={handleCopy} />
                <SecretField label="Contenu du QR" value={created.qr_payload} onCopy={handleCopy} />
              </Grid>
            </>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Ce bouton crée une identité matérielle NUE, activée par un QR : c&rsquo;est
                  le chemin d&rsquo;une box de foyer, ou d&rsquo;un banc d&rsquo;essai. Le serveur
                  d&rsquo;un établissement se prépare depuis « Clés de pose », qui le
                  rattache au site et produit son ordre de fabrication.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Matériel"
                  value={createForm.hardware_model}
                  onChange={e => setCreateForm(f => ({ ...f, hardware_model: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Notes" multiline rows={2}
                  value={createForm.notes}
                  onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {created ? (
            <>
              <Button startIcon={<PrintIcon />} variant="outlined" onClick={printSticker}>
                Imprimer l&rsquo;étiquette
              </Button>
              <Button variant="contained" onClick={() => closeCreateDialog()}>
                J&rsquo;ai imprimé / copié — Fermer
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => closeCreateDialog()} disabled={creating}>Annuler</Button>
              <Button variant="contained" onClick={handleCreate} disabled={creating}>
                {creating ? <CircularProgress size={18} /> : 'Créer'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast} autoHideDuration={6000} onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
